import React, { useRef, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Button } from "primereact/button";
import _ from "lodash";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { actionBodyTemplate, columnContent } from "./service";
import '../../../assets/styles/dataGrid.scss';
import editIcon from '../../../assets/images/editIcon.svg';
import InputComponent from "../inputComponent";
import { SORT_ORDER } from '../../../constants/sortOrder';

export default function DataGrid({
  value,
  column,
  loading,
  alldata,
  dataKey,
  urlPath,
  sortable,
  selection,
  concernId,
  navigator,
  selectable,
  excColumns,
  tableStyle,
  optionsView,
  isShowEdit,
  filterColumn = [],
  selectionMode = "multiple",
  columnClickable,
  detailUrlPrefix,
  tableClassName,
  expandedRows,
  customColumn,
  isAccordionEnabled,
  isPaginatorEnabled = true,
  showDownloadButton = true,
  resizableColumns = false,
  onRowClick = () => {},
  onPageChange = () => {},
  onSortClick = () => {},
  onRowCountChange = () => {},
  onSelectionChange = () => {},
  onRowToggle = () => {},
  onRowExpand = () => {},
  onRowCollapse = () => {},
  rowExpansionTemplate = () => {},
  allowExpansion = () => {},
  columnCallBack = () => {},
  ...props
}) {
  const dt = useRef(null);

  const exportCSV = () => {
    dt.current.exportCSV();
  };


  const exportExcel = () => {
    const exportData = value.map((item) => {
      const row = {};
      column.forEach((col) => {
        const label = col.label?.trim();
        const field = col.name;

        const cellValue = _.get(item, field);
        row[label] =
          cellValue !== undefined && cellValue !== null ? cellValue : "N/A";
      });
      return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, "data_export.xlsx");
  };

  const exportPdf = () => {
    const doc = new jsPDF();

    const headers = column.map((col) => col.label.trim());

    const rows = value.map((item) =>
      column.map((col) => {
        const cellValue = _.get(item, col.name);
        return cellValue !== undefined && cellValue !== null
          ? cellValue
          : "N/A";
      })
    );

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 20,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("data_export.pdf");
  };

  const leftContent = (e) => {
    const first = Number.isFinite(e?.first) ? e.first : 0;
    const rows = Number.isFinite(e?.rows) ? e.rows : 0;
    const total = Number.isFinite(e?.totalRecords) ? e.totalRecords : 0;
    const currentMax = first + rows;
    return (
      <div className="flex rc-patient-flex">
        <div className="rc-patient-option-rows" style={{ position: 'relative', zIndex: 9999 }}>
          <InputComponent
            type="dropdown-std"
            placeholder="select a range"
            options={[5, 10, 25, 50, 100].map((n) => ({ label: String(n), value: n }))}
            value={navigator?.rows || 1}
            onChange={onRowCountChange}
          />
        </div>
        <span className="pl-3 text-commonConten public_sans_400 text-15px flex align-items-center">
          {`Showing ${Math.min(first + 1, Math.max(total, 1))} - ${Math.min(
            currentMax,
            total
          )} of ${total}`}
        </span>
      </div>
    );
  };

  const customActionBodyTemplate = (rowData, options) => {
    if (!_.isArray(optionsView) || _.isEmpty(optionsView)) {
      return null;
    }

    return (
      <div className="table-ellipses-actions">
        {actionBodyTemplate(rowData, optionsView, {
          popup: true,
          popupAlignment: "right",
          autoZIndex: true,
          baseZIndex: 10000,
        })}
      </div>
    );
  };

  return (
    <div className="md:primary-box-shadow bg-white border-round-md" style={{ position: 'relative', zIndex: 1 }}>
      {props?.header}

      <div className="data__grid-container">
        <DataTable
          ref={dt}
          resizableColumns={resizableColumns}
          editMode="row"
          onSort={onSortClick}
          sortField={navigator?.sortBy}
          removableSort={props?.removableSort}
          sortOrder={navigator?.sortDir === SORT_ORDER.ASC ? 1 : -1}
          onRowClick={onRowClick}
          value={value}
          dataKey={dataKey}
          selection={selection}
          selectionMode="checkbox"
          onSelectionChange={onSelectionChange}
          tableStyle={{ ...tableStyle, position: 'relative', zIndex: 1 }}
          className="data-grid-table-body"
          tableClassName={tableClassName}
          emptyMessage="No results found."
          scrollable
          loading={loading}
          expandedRows={expandedRows}
          onRowToggle={onRowToggle}
          onRowExpand={onRowExpand}
          onRowCollapse={onRowCollapse}
          rowExpansionTemplate={rowExpansionTemplate}
          allowExpansion={allowExpansion}
        >
          {isAccordionEnabled && <Column expander style={{ width: "5rem" }} />}
          {selectable && (
            <Column
              selectionMode={selectionMode}
              className="selection-check-box"
              headerStyle={{ width: "3rem" }}
            />
          )}

          {columnContent(
            "bigBold",
            column,
            detailUrlPrefix,
            concernId,
            columnClickable,
            urlPath,
            filterColumn,
            excColumns,
            sortable,
            customColumn,
            columnCallBack
          )}
          
          {isShowEdit && (
            <Column
              body={(event) => (
                <button
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => props?.onEdit?.(event)}
                >
                  <img src={editIcon} alt="Edit" />
                </button>
              )}
            />
          )}

          {_.isArray(optionsView) && !_.isEmpty(optionsView) && (
            <Column
              body={customActionBodyTemplate}
              exportable={false}
              className="action-column"
              style={{ width: '60px', textAlign: 'center' }}
            />
          )}
        </DataTable>

        {isPaginatorEnabled && (
          <Paginator
            leftContent={leftContent}
            first={navigator?.first || 0}
            rows={navigator?.rows || 1}
            totalRecords={navigator?.totalRecords || 1}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </div>
  );
}